import React, { useState, useEffect, useRef, useMemo } from "react";
import PurchaseRequisition from "./reusable/purchase-requisition";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import loader2 from "../../images/loader_2.gif";
import {
  getCurrentDateToday,
  convertTo12HourPlusDateFormat,
  formatToPeso,
} from "../../util/helperFunctions";
import "../../styles/epr.css";

function EprForm({
  formDetails,
  setFormSubmitted,
  setReferenceId,
  setDateAndTime,
}) {
  const {
    id,
    purchase_requisition_company_detail = [],
    purchase_requisition_pr_detail = [],
  } = formDetails;

  const [requisitions, setRequisitions] = useState([{ id: 1 }]);
  const [purchaseType, setPurchaseType] = useState("");
  const [purchaseTypeText, setPurchaseTypeText] = useState("");
  const [company, setCompany] = useState("");
  const [totalCost, setTotalCost] = useState(0);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const recommendedSupplier = useRef(null);
  const urgent = useRef(null);
  const dateNeeded = useRef(null);

  const btnSubmitText = useRef(null);
  const btnLoaderImg = useRef(null);

  const selectCompanyRef = useRef(null);
  const purchaseRequestRef = useRef(null);

  const addRequisition = () => {
    setIsDirty(true);
    const newRequisition = {
      id: new Date().getTime(),
      description: "",
      remarks: "",
      quantity: 0,
      price: 0,
      cost: 0,
      attachment: [],
      file: [],
    };
    setRequisitions([...requisitions, newRequisition]);
  };

  const removeRequisition = (removeId) => {
    setIsDirty(true);
    setRequisitions(
      requisitions.filter((requisition) => requisition.id !== removeId),
    );
  };

  const updateRequisition = (updatedRequisition) => {
    setIsDirty(true);
    setRequisitions((prevRequisitions) =>
      prevRequisitions.map((requisition) =>
        requisition.id === updatedRequisition.id
          ? updatedRequisition
          : requisition,
      ),
    );
  };

  const handlePurchaseRequestOnChange = (e) => {
    setIsDirty(true);
    setPurchaseType(e.target.value);
    setPurchaseTypeText(e.target.selectedOptions[0].textContent);
  };

  const handleCompanyOnChange = (e) => {
    setIsDirty(true);
    setCompany(e.target.value);
  };

  const handleTotalCostChange = () => {};

  useEffect(() => {
    const total = requisitions.reduce((sum, req) => {
      return sum + (parseFloat(req.cost) || 0);
    }, 0);

    setTotalCost(total);
  }, [requisitions]);

  const totalQuantity = useMemo(() => {
    return requisitions.reduce((sum, req) => {
      return sum + (parseFloat(req.quantity) || 0);
    }, 0);
  }, [requisitions]);

  const totalFiles = useMemo(() => {
    return requisitions.reduce((sum, req) => {
      const attachmentCount = req?.attachment?.length || 0;
      const fileCount = req?.file?.length || 0;
      return sum + attachmentCount + fileCount;
    }, 0);
  }, [requisitions]);

  const hasLineItems = requisitions.length > 0;

  useEffect(() => {
    const hasMeaningfulLineItem = requisitions.some((req) => {
      return (
        (req?.description || "").trim() !== "" ||
        (req?.remarks || "").trim() !== "" ||
        Number(req?.quantity || 0) > 0 ||
        Number(req?.price || 0) > 0 ||
        (req?.attachment?.length || 0) > 0 ||
        (req?.file?.length || 0) > 0
      );
    });

    const hasTopLevelInput =
      company !== "" ||
      purchaseType !== "" ||
      (recommendedSupplier.current?.value || "").trim() !== "" ||
      Boolean(urgent.current?.checked) ||
      (dateNeeded.current?.value || "") !== getCurrentDateToday();

    if (hasTopLevelInput || hasMeaningfulLineItem) {
      setIsDirty(true);
    }
  }, [company, purchaseType, requisitions]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!isDirty || isSubmitting) return;

      event.preventDefault();
      event.returnValue = "You have unsaved changes. Are you sure you want to leave?";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty, isSubmitting]);

  const handleFormValidation = () => {
    let valid = true;
    const selectCompanyValue = selectCompanyRef.current.value;
    const purchaseRequestValue = purchaseRequestRef.current.value;

    const descriptionElemments = document.querySelectorAll(".Description");
    const quantityElements = document.querySelectorAll(".Quantity");

    if (selectCompanyValue === "DEFAULT") {
      selectCompanyRef.current.classList.add("invalid-input-data");
      valid = false;
    }
    if (purchaseRequestValue === "DEFAULT") {
      purchaseRequestRef.current.classList.add("invalid-input-data");
      valid = false;
    }

    if (descriptionElemments.length > 0 && quantityElements.length > 0) {
      descriptionElemments.forEach((el) => {
        const elementValue = el.value;
        if (elementValue.trim() === "") {
          el.classList.add("invalid-input-data");
          valid = false;
        }
      });
      quantityElements.forEach((el) => {
        const elementValue = el.value;
        if (elementValue.trim() === "" || elementValue == 0) {
          el.classList.add("invalid-input-data");
          valid = false;
        }
      });
    }
    if (!valid) showSwal(common.errors.UNFILLED_INPUTS_DATA_ERROR);
    return valid;
  };

  const handleSubmitRequest = async () => {
    const validForm = handleFormValidation();
    if (!validForm) return;
    if (isSubmitting) return;
    setSubmitting(true);
    btnSubmitText.current.style.display = "none";
    btnLoaderImg.current.style.display = "inline-block";

    const data = {
      company_id: company,
      recommended_supplier: recommendedSupplier.current.value,
      purchase_request_id: purchaseType,
      urgent: urgent.current.checked,
      date_needed: dateNeeded.current.value,
      requisitions,
    };
    const formData = new FormData();

    formData.append("company_id", data.company_id);
    formData.append("recommended_supplier", data.recommended_supplier);
    formData.append("purchase_request_id", data.purchase_request_id);
    formData.append("urgent", data.urgent);
    formData.append("date_needed", data.date_needed);

    data.requisitions.forEach((req, index) => {
      formData.append(`requisitions[${index}][id]`, req.id);
      formData.append(`requisitions[${index}][description]`, req.description);
      formData.append(`requisitions[${index}][remarks]`, req.remarks);
      formData.append(`requisitions[${index}][quantity]`, req.quantity);
      formData.append(`requisitions[${index}][price]`, req.price);
      formData.append(`requisitions[${index}][cost]`, req.cost);

      req.attachment.forEach((attachment, attIndex) => {
        formData.append(
          `requisitions[${index}][attachment][${attIndex}][filePreview]`,
          attachment.filePreview,
        );
        formData.append(
          `requisitions[${index}][attachment][${attIndex}][file]`,
          attachment.file,
        );
        formData.append(
          `requisitions[${index}][attachment][${attIndex}][type]`,
          attachment.type,
        );
        formData.append(
          `requisitions[${index}][attachment][${attIndex}][size]`,
          attachment.size,
        );
      });

      req.file.forEach((file, fileIndex) => {
        formData.append(
          `requisitions[${index}][file][${fileIndex}][filePreview]`,
          file.filePreview,
        );
        formData.append(
          `requisitions[${index}][file][${fileIndex}][file]`,
          file.file,
        );
        formData.append(
          `requisitions[${index}][file][${fileIndex}][type]`,
          file.type,
        );
        formData.append(
          `requisitions[${index}][file][${fileIndex}][size]`,
          file.size,
        );
      });
    });

    try {
      setSubmitting(true);
      btnSubmitText.current.style.display = "none";
      btnLoaderImg.current.style.display = "inline-block";

      const response = await ApiService.forms.submitFormRequest(id, formData);

      const { success, requisition } = response;

      if (!success) {
        throw new Error(common.errors.SUBMIT_EPR_FORM_ERROR);
      }

      setReferenceId(requisition.reference_id);
      setDateAndTime(convertTo12HourPlusDateFormat(requisition.createdAt));
      setFormSubmitted(true);
      setIsDirty(false);
    } catch (error) {
      console.error("Submit Form Error:", error);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        common.errors.SUBMIT_EPR_FORM_ERROR;

      showSwal(message);
    } finally {
      setSubmitting(false);
      if (btnSubmitText.current) btnSubmitText.current.style.display = "block";
      if (btnLoaderImg.current) btnLoaderImg.current.style.display = "none";
    }
  };

  return (
    <div className="epr-form-shell">
      <div className="epr-form-intro">
        <span className="epr-form-intro__eyebrow">Create Request</span>
        <h3>Submit a new request for approval</h3>
        <p>
          Complete the required details below. You can add multiple line items,
          attach supporting files, and review the total before submitting.
        </p>
      </div>

      <div className="epr-form-layout">
        <div className="epr-form">
          <section className="epr-section" aria-labelledby="epr-request-setup-title">
            <div className="epr-section__header">
              <h4 id="epr-request-setup-title">Step 1: Request Setup</h4>
              <span>Required fields are marked with *</span>
            </div>

            <div className="epr-form-column epr-form-column--two">
              <div className="epr-form-row">
                <label htmlFor="epr-company-select">
                  Select Company <span className="epr-required">*</span>
                </label>
                <select
                  id="epr-company-select"
                  name="company"
                  defaultValue={"DEFAULT"}
                  onChange={(e) => handleCompanyOnChange(e)}
                  required
                  ref={selectCompanyRef}
                >
                  <option value="DEFAULT" disabled>
                    Select a company
                  </option>
                  {purchase_requisition_company_detail.map((prc, index) => (
                    <option value={prc.id} key={index}>
                      {prc.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="epr-form-row">
                <label htmlFor="epr-type-select">
                  Type of Request <span className="epr-required">*</span>
                </label>
                <select
                  id="epr-type-select"
                  name="requestType"
                  defaultValue={"DEFAULT"}
                  onChange={(e) => handlePurchaseRequestOnChange(e)}
                  ref={purchaseRequestRef}
                >
                  <option value="DEFAULT" disabled>
                    Select a purchase type
                  </option>
                  {purchase_requisition_pr_detail.map((pr, index) => (
                    <option value={pr.id} key={index}>
                      {pr.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <section className="epr-section" aria-labelledby="epr-fulfillment-title">
            <div className="epr-section__header">
              <h4 id="epr-fulfillment-title">Step 2: Fulfillment and Priority</h4>
            </div>

            <div className="epr-form-column epr-form-column--two">
              <div className="epr-form-row">
                <label htmlFor="epr-date-needed">Date Needed</label>
                <input
                  id="epr-date-needed"
                  type="date"
                  ref={dateNeeded}
                  defaultValue={getCurrentDateToday()}
                  onChange={() => setIsDirty(true)}
                />
              </div>

              <div className="epr-form-row">
                <label htmlFor="epr-recommended-supplier">Recommended Supplier</label>
                <input
                  id="epr-recommended-supplier"
                  type="text"
                  ref={recommendedSupplier}
                  onChange={() => setIsDirty(true)}
                  placeholder="Optional supplier recommendation"
                />
              </div>
            </div>

            <div className="epr-form-column epr-form-column--single">
              <div className="epr-form-row epr-form-row--checkbox">
                <div className="epr-form-checkbox">
                  <input
                    type="checkbox"
                    id="urgent"
                    ref={urgent}
                    onChange={() => setIsDirty(true)}
                  />
                  <label htmlFor="urgent">Mark as urgent</label>
                </div>
              </div>
            </div>
          </section>

          <section className="epr-section" aria-labelledby="epr-items-title">
            <div className="epr-section__header epr-section__header--with-action">
              <div>
                <h4 id="epr-items-title">Step 3: What do you need?</h4>
                <p>{purchaseTypeText || "Add one or more line items for this request."}</p>
              </div>
              <button type="button" className="epr-add-item-btn" onClick={addRequisition}>
                <i className="fa fa-plus" aria-hidden="true"></i>
                <span>Add Item</span>
              </button>
            </div>

            <div className="purchase-requisition-dynamic">
              <div className="purchase-requisition-fields">
                {hasLineItems ? (
                  requisitions.map((requisition, index) => (
                    <PurchaseRequisition
                      key={requisition.id}
                      id={requisition.id}
                      itemIndex={index}
                      onRemove={removeRequisition}
                      onAdd={addRequisition}
                      handleTotalCostChange={handleTotalCostChange}
                      updateRequisition={updateRequisition}
                    />
                  ))
                ) : (
                  <div className="epr-empty-line-items">
                    <h5>No items added yet</h5>
                    <p>
                      Add the products, services, or materials you are requesting.
                    </p>
                    <button type="button" className="epr-add-item-btn" onClick={addRequisition}>
                      <i className="fa fa-plus" aria-hidden="true"></i>
                      <span>Add Item</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>

          <section className="epr-section" aria-labelledby="epr-review-title">
            <div className="epr-section__header">
              <h4 id="epr-review-title">Step 4: Review and Submit</h4>
              <span>Your request will be submitted for approval.</span>
            </div>

            <div className="submit-action-btn">
              <button
                className="search-btn submit-request"
                onClick={() => handleSubmitRequest()}
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                <span className="form-btn-txt" ref={btnSubmitText}>
                  {isSubmitting ? "Submitting Request..." : "Submit Request"}
                </span>
                <img
                  src={loader2}
                  className="form-loader"
                  ref={btnLoaderImg}
                  style={{ display: "none" }}
                  alt="Submitting request"
                />
              </button>
            </div>
          </section>
        </div>

        <aside className="epr-summary-panel" aria-label="Request Summary">
          <div className="epr-summary-panel__block">
            <span className="epr-summary-panel__eyebrow">Request Summary</span>
            <h4>Review at a glance</h4>

            <div className="epr-summary-list">
              <div className="epr-summary-row">
                <span>Request Type</span>
                <strong>{purchaseTypeText || "Not selected"}</strong>
              </div>
              <div className="epr-summary-row">
                <span>Items</span>
                <strong>{requisitions.length}</strong>
              </div>
              <div className="epr-summary-row">
                <span>Total Quantity</span>
                <strong>{totalQuantity}</strong>
              </div>
              <div className="epr-summary-row">
                <span>Attachments/Files</span>
                <strong>{totalFiles}</strong>
              </div>
            </div>

            <div className="epr-total-cost-highlight">
              <span>Estimated Total</span>
              <strong>{formatToPeso(totalCost || 0)}</strong>
            </div>

            <p className="epr-summary-note">
              Check required fields and line-item details before submitting.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default EprForm;
